import React from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import colorString from 'color-string';
import './first-global.scss';
import './icons';
import themeImages from './images';
import favicon from './images/favicon.png';
import baseCustomTheme from '../Base/baseTheme';

import '@fontsource/montserrat/latin.css';
import { getBrandImage } from '../../utils/helper';

// Colours
const primaryColor = '0, 173, 238';
const primaryDarkColor = '22, 68, 152';
const primaryDarkerColor = '45, 51, 146';
const primaryLightColor = '0, 173, 238';
//   const primaryColorContrastText = '255, 255, 255';
const successColor = 'rgb(34,139,34)';
//  const primaryColor = "#2B5CFF";
const white = '255, 255, 255';
const secondaryColor = '233, 122, 59';
const secondaryDarkColor = '233, 122, 59';
const warningColor = '254, 224, 0';
const warningColorContrastTest = '23, 23, 37';
const infoColor = '222, 235, 255';
const infoColorContrastText = '23, 23, 37';
//  const secondaryDarkColor = '254, 224, 0';
//  const lightColor = "#f1f1f5";
//  const backgroundColor = "#fafafb";
//  const borderColor = "#e2e2ea";
//  const baseFontColor = "#565656";
//  const mediumBlack = "#54545f";
//  const checkboxText = "#83838d";
//  const stepBorder = "#d5d5dc";
//  const textColor = "#171725";
// const menuItemColor = '34, 119, 255';
const textColor = 'rgba(60, 60, 60, 1)';

const fontFamily1 = "'Montserrat', sans-serif";
const fontFamily2 = "'Montserrat', sans-serif";
const iconFontFamily = '"Font Awesome 5 Pro"';
const fontSize = 16; // px
// Tell Material-UI what's the font-size on the html element.
// 16px is the default font-size used by browsers.
const htmlFontSize = 16;
const coef = fontSize / 14;

/**
 * Set defaults
 */
const theme = createTheme({
  shape: {
    borderRadius: 10,
  },
  spacing: factor => `${0.25 * factor}rem`,
  palette: {
    common: {
      black: '#000',
      white: '#FFF',
    },
    type: 'light',
    primary: {
      light: `rgb(${primaryLightColor})`,
      main: `rgb(${primaryColor})`, // primary color
      dark: `rgb(${primaryDarkColor})`,
      selected: `rgba(${primaryColor}, 0.1)`,
      contrastText: `rgb(${white})`,
    },
    secondary: {
      light: `rgb(${primaryColor})`,
      main: `rgb(${secondaryColor})`, // primary color
      dark: `rgb(${primaryDarkColor})`,
      contrastText: `rgb(${primaryDarkColor})`,
    },
    error: {
      light: 'rgb(240,128,128)',
      main: 'rgb(220,20,60)', // primary color
      dark: 'rgb(178,34,34)',
      contrastText: 'rgb(255,255,224)',
    },
    warning: {
      light: `rgb(${warningColor})`,
      main: `rgb(${warningColor})`,
      dark: `rgb(${warningColor})`,
      contrastText: `rgb(${warningColorContrastTest})`,
    },
    info: {
      light: 'rgb(135,206,250)',
      main: 'rgb(135,206,235)', // primary color
      dark: 'rgb(0,191,255)',
      contrastText: 'rgb(25,25,112)',
    },
    success: {
      light: 'rgb(50,205,50)',
      main: successColor, // primary color
      dark: 'rgb(0,100,0)',
      contrastText: 'rgb(152,251,152)',
    },
    background: {
      paper: `rgb(${white})`,
      default: 'rgb(255,255,240)',
    },
    grey: {
      50: '#f5f8fa',
      100: '#f1f1f5',
      200: '#d5d5dc',
      300: '#a4a7a9', //'#e2e2ea',
      400: '#b5b5be',
      500: '#666666',
      600: '#757575',
      700: '#54545f',
      800: '#44444f',
      900: '#171725',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
  },
});

theme.palette.dark = {
  main: `rgb(${primaryDarkColor})`,
};

theme.palette.miscellaneous = {
  bannerContrastText: `rgb(${primaryDarkColor})`,
  planFeaturesIcon: theme.palette.primary.contrastText,
};
theme.palette.stepper = {
  bgComplete: `rgb(${secondaryColor})`,
  textComplete: `rgb(${white})`,
};

/**
 * Typography
 */
theme.typography = {
  pxToRem: size => `${(size / htmlFontSize) * coef}rem`,
  fontFamily: fontFamily2,
  iconFontFamily: iconFontFamily,
  headingFontFamily: fontFamily1,
  h1: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '26px',
    lineHeight: '36px',
    letterSpacing: '-1px',
    textTransform: 'initial',
    [theme.breakpoints.up('sm')]: {
      fontSize: '3rem',
      lineHeight: '4.42rem',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: '4rem',
      lineHeight: '5.42rem',
    },
  },
  h2: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '-1px',
    textTransform: 'initial',
    [theme.breakpoints.up('sm')]: {
      fontSize: '36px',
      lineHeight: '54px',
    },
  },
  h3: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '22px',
    lineHeight: 1.2,
    letterSpacing: '-1px',
    textTransform: 'initial',
    color: theme.palette.primary.main,
    [theme.breakpoints.up('sm')]: {
      fontSize: '24px',
      lineHeight: '36px',
    },
  },
  h4: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '27px',
    // letterSpacing: '0.00735em',
    textTransform: 'initial',
  },
  h5: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: 1.2,
    textTransform: 'initial',
    letterSpacing: '-0.0125em',
  },
  h6: {
    fontFamily: fontFamily1,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineGeight: 1.2,
    // letterSpacing: '0.8px',
  },
  body1: {
    fontFamily: fontFamily2,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '18px',
    lineHeight: '28px',
    // letterSpacing: '0.00938em',
  },
  body2: {
    fontFamily: fontFamily2,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '16px',
    lineHeight: '22px',
    // letterSpacing: '0.01071em',
  },
  subtitle1: {
    fontFamily: fontFamily2,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '12px',
    lineHeight: '18px',
    // color: $dark-black;
  },
  subtitle2: {
    fontFamily: fontFamily2,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '18px',
    // color: $dark-black;
  },
  caption: {
    fontFamily: fontFamily2,
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '16px',
    lineHeight: '22px',
  },

  button: {
    fontFamily: fontFamily1,
    fontSize: '14px',
    lineHeight: 1.2,
    fontWeight: 700,
    fontStyle: 'normal',
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    textTransform: 'initial',
  },
  icons: {
    checkboxStyle: 'fas',
    planFeatures: ['fas', 'check-circle'],
    accordionExpand: ['fas', 'angle-down'],
  },
};

/**
 * Set theme specifics
 */
theme.overrides = {
  MuiAppBar: {
    colorPrimary: {
      backgroundColor: '#FFF',
      boxShadow: 'inset 0 -1px 0 #e2e2ea',
    },
    colorSecondary: {
      backgroundColor: theme.palette.dark.main,
      color: theme.palette.common.white,
      '&.selected-plan-button-wrapper .product-scroll .links a': {
        color: theme.palette.common.white,
      },
    },
  },
  MuiCard: {
    root: {
      boxShadow: ' 3px 6px 21px rgba(205, 205, 212, 0.296848)',
      borderRadius: theme.shape.borderRadius,
    },
  },
  MuiContainer: {
    root: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
      },
    },
  },
  MuiFormControl: {
    root: {
      fontFamily: theme.typography.fontFamily,
    },
  },
  MuiFormLabel: {
    root: {
      fontSize: '15px',
      fontFamily: theme.typography.fontFamily,
    },
  },
  MuiInputBase: {
    root: {
      fontSize: '15px',
      fontFamily: theme.typography.fontFamily,
    },
  },
  MuiAutocomplete: {
    option: {
      position: 'relative',

      '&:active': {
        backgroundColor: theme.palette.grey['50'],
      },
      '&[data-focus="true"]': {
        backgroundColor: theme.palette.grey['50'],
      },
      '.icon-marker': {
        color: theme.palette.grey['400'],
        marginRight: '1rem',
        fontSize: '1.75rem',
      },
    },
    input: {
      paddingTop: `14.5px !important`,
      paddingBottom: `14.5px !important`,
    },
    inputRoot: {
      '&[class*="MuiOutlinedInput-root"]': {
        paddingTop: '0',
        paddingBottom: '0',
      },
    },

    root: {
      '&[class*="search-input"] $label': {
        fontSize: '18px !important',
      },
      '&[class*="search-input"] $input': {
        fontSize: '20px',
        padding: '19px 8px !important',
      },
    },

    paper: {
      borderRadius: theme.shape.borderRadius,
    },
  },
  MuiOutlinedInput: {
    root: {
      // fontFamily: theme.typography.fontFamily,
      // borderRadius: 0,
      '&$focused $notchedOutline': {
        borderWidth: '1px',
        borderColor: `rgb(${primaryColor})`,
      },
      '&:hover $notchedOutline': {
        borderColor: `rgba(${primaryColor}, 0.3)`,
      },
      '&$input': {
        paddingTop: '14.5px',
        paddingBottom: '14.5px',
      },
    },
    input: {
      paddingTop: '14.5px',
      paddingBottom: '14.5px',
    },
    notchedOutline: {
      borderColor: theme.palette.grey['200'],
    },
  },
  MuiInputAdornment: {
    root: {
      '&$positionEnd': {
        cursor: 'pointer',
      },
    },
  },
  MuiFormHelperText: {
    root: {
      '&$contained': {
        lineHeight: '16px',
        letterSpacing: '0.1px',
        // color: "$darkGrey",
        fontSize: '13px',
        flexBasis: '100%',
        marginTop: '3px',
        marginLeft: '14px',
        marginRight: '14px',
      },
      '&$contained &$error': {
        //  color: $errorColor;
      },
    },
  },
  MuiButton: {
    root: {
      // textTransform: 'initial',
      padding: `${theme.spacing(3.5, 5)} !important`,
      [theme.breakpoints.down('md')]: {
        // paddingLeft: `${theme.spacing(2.5)} !important`,
        // paddingRight: `${theme.spacing(2.5)} !important`,
      },
    },
    text: {
      padding: `${theme.spacing(3.5, 5)}`,
    },
    textPrimary: {},
    containedPrimary: {
      '&:hover': {
        background: `rgba(${primaryLightColor}, 0.12)`,
      },
      '&$disabled': {
        background: `rgba(${primaryDarkColor}, 0.12)`,
        color: `rgba(${primaryDarkColor}, 0.26)`,
      },
      '&.planFilter-search-button': {
        padding: `${theme.spacing(4.7, 5)} !important`,
        color: theme.palette.common.white,
      },
    },
    outlinedPrimary: {
      '&:hover': {},
    },
    outlinedSecondary: {
      '&$disabled': {
        color: `rgb(${secondaryColor})`,
        border: `1px solid rgba(${secondaryColor}, 0.5)`,
      },
      '&:hover': {},
    },
    sizeLarge: {
      padding: `${theme.spacing(3.5, 5)}`,
      fontSize: '20px',
    },
  },
  MuiAlert: {
    root: {
      margin: '20px auto',
      '&$icon': {
        alignItems: 'center',
      },
    },
  },
  MuiCircularProgress: {
    root: {
      color: successColor,
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: '-12px',
      marginLeft: '-12px',
    },
  },
  MuiPaper: {
    root: {
      '&.card': {
        background: theme.palette.common.white,
        boxShadow: 'none',
        [theme.breakpoints.up('md')]: {
          background: theme.palette.common.white,
          padding: '30px',
          marginBottom: '50px',

          boxShadow: '3px 6px 21px rgb(205 205 212 / 30%)',
        },
      },
      '&.service-card.card': {
        [theme.breakpoints.up('md')]: {
          padding: '0',
        },
      },
    },
    elevation2: {
      border: '1px solid #b5b5be',
    },
    rounded: {
      borderRadius: '10px',
    },
  },
  MuiDialog: {
    paper: {
      paddingBottom: '16px',
    },
  },
  MuiRadio: {
    root: {
      color: theme.palette.grey['500'],
      '& ~ .MuiFormControlLabel-label': {
        borderWidth: '1px',
        borderStyle: 'solid',
        background: theme.palette.grey['100'],
        borderColor: theme.palette.grey['100'],
        color: theme.palette.grey['500'],
        borderRadius: theme.shape.borderRadius,
      },
      '&$checked': {
        color: theme.palette.primary.main,
        '& ~ .MuiFormControlLabel-label': {
          background: theme.palette.primary.dark,
          borderColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
        },
      },
    },
    colorSecondary: {
      '&$checked': {
        color: theme.palette.primary.main,
      },
    },
  },
  MuiSvgIcon: {
    root: {
      fontSize: '1.375rem',
    },
  },
  MuiMenuItem: {
    root: {
      fontSize: '15px',
    },
  },
  MuiTooltip: {
    tooltip: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey['300']}`,
      boxShadow: '3px 6px 21px rgba(205, 205, 212, 0.296848)',
      // borderRadius: 0,
      fontFamily: fontFamily1,
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: '14px',
      lineHeight: '21px',
      padding: '16px',
      color: theme.palette.grey['700'],
    },
    arrow: {
      color: theme.palette.common.white,
      // '&:after': {
      //   width: '0',
      //   height: '0',
      //   margin: 'auto',
      //   content: '""',
      //   display: 'block',
      //   borderStyle: 'solid',
      // },
    },
    tooltipPlacementBottom: {
      // '&$arrow:before': {
      //   position: 'relative',
      //   bottom: '0',
      // },
      '&$arrow': {
        ':after': {
          // borderStyle: 'solid',
          // borderWidth: '0 1.25em 1.25em 1.25em',
          borderColor: `transparent transparent ${theme.palette.grey['300']} transparent`,
          // bottom: '0px',
          // position: 'absolute',
          // zIndex: '-1',
          // left: '-0.35em',
        },
      },
    },
    tooltipPlacementTop: {
      '&$arrow': {
        '&:before': {
          position: 'relative',
          bottom: '0',
        },
        '&:after': {
          borderStyle: 'solid',
          borderWidth: '1.25em 1.25em 0 1.25em',
          borderColor: `${theme.palette.grey['300']} transparent transparent transparent`,
          bottom: '-3px',
          position: 'absolute',
          zIndex: '-1',
          left: '-0.35em',
        },
      },
    },
  },
  MuiAccordionSummary: {
    root: {
      color: theme.palette.primary.main,
    },
  },
  MuiCheckbox: {
    root: {
      color: theme.palette.grey['400'],
    },
  },
  MuiSnackbar: {
    root: {
      borderRadius: theme.shape.borderRadius,
    },
  },
  MuiFormControlLabel: {
    root: {
      '&.checkbox-group': {
        '& .MuiCheckbox-root ~ .MuiFormControlLabel-label': {
          background: theme.palette.grey['100'],
          border: `1px solid ${theme.palette.grey['100']}`,
          color: theme.palette.grey['500'],
          borderRadius: theme.shape.borderRadius,
          padding: '10px 20px 10px 40px',
          fontStyle: 'normal',
          fontWeight: '600',
          fontSize: '14px',
          lineHeight: '28px',
          height: '48px',
          fontFamily: theme.typography.headingFontFamily,
        },
        '& .MuiCheckbox-root.Mui-checked ~ .MuiFormControlLabel-label': {
          background: `rgba(${primaryColor}, 0.1)`,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        },
      },
    },
  },
};

// references ../Base/baseTheme.js
theme.custom = baseCustomTheme(theme, {
  Common: {
    textColor: textColor,
  },
  AppBar: {
    backgroundColor: theme.palette.common.white,
  },
  ComparePlan: {
    postcodeHeading: {
      color: theme.palette.secondary.main,
    },
  },
  PlanFilter: {
    background: `linear-gradient(90deg, rgb(${primaryDarkerColor}) 0%, rgb(${primaryColor}) 100%)`,
    filters: {
      menuButtonCss: {
        border: '1px solid white',
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.secondary.main,
        '&:hover': {
          backgroundColor: theme.palette.secondary.dark,
        },
        '&:focus': {
          backgroundColor: theme.palette.secondary.dark,
        },
      },
      textCss: {
        fontWeight: 700,
        fontFamily: fontFamily1,
      },
      d2dCheckboxCss: {
        '.custom-checkbox': {
          color: theme.palette.primary.contrastText,
        },
        '.MuiFormControlLabel-label': {
          color: theme.palette.primary.contrastText,
        },
      },
    },
    searchField: {
      helperTextCss: {
        fontSize: 14,
        fontWeight: 400,
        color: theme.palette.common.white,
        fontFamily: fontFamily2,
      },
      fieldCss: {
        fontFamily: fontFamily2,
        borderRadius: theme.shape.borderRadius,
        // border: '1px solid transparent',
      },
      fieldIconCss: {
        color: `rgb(${theme.palette.secondary.main})`,
      },
      buttonCss: {
        fontFamily: fontFamily1,
        color: theme.palette.common.white,
        border: '1px solid white',
        paddingTop: `${theme.spacing(4.7)} !important`,
        paddingBottom: `${theme.spacing(4.7)} !important`,
        borderRadius: theme.shape.borderRadius,
        fontSize: 20,
        fontWeight: 700,
        background: theme.palette.secondary.main,
        '&:hover': {
          color: theme.palette.common.white,
          background: theme.palette.secondary.dark,
        },
        '&:disabled': {
          color: theme.palette.common.white,
          background: theme.palette.secondary.main,
        },
      },
    },
  },
  PromoPopup: {
    PromoPopupLinkCss: {
      color: theme.palette.common.white,
      fontSize: 16,
      '&.promo-applied': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    PromoPopupTextCss: {
      color: textColor,
    },
    buttonCss: {
      paddingTop: '14px !important',
      paddingBottom: '14px !important',
    },
  },
  BasicDetails: {
    BasicDetailsTopCss: {
      '.basic-details__summary-subheading': {
        fontSize: '22px',
      },
      '.basic-details__summary-heading': {
        textTransform: 'initial',
        fontSize: '36px',
        '@media screen and (min-width: 768px)': {
          fontSize: '39px',
        },
      },
    },
    BasicDetailsBottomCss: {
      '.estimated-cost-strike strike': {
        fontSize: '28px',
        fontWeight: 600,
      },
      '.estimated-cost-figure h3': {
        fontSize: '32px',
      },
      '.estimated-cost-terms p': {
        fontSize: '12.5px',
        color: theme.palette.grey[700],
      },
      '.price-details-desc .reference_price, .price-details-desc .reference_price_without_discounts, .price-details-desc .distribution_area, .price-details-desc .retail_tariff, .price-details-desc .annual_usage, .price-details-desc .units, .price-details-desc .market_segment, .price-details-desc .price_difference':
        {
          fontWeight: '600 !important',
          color: theme.palette.grey[800],
        },
    },
  },
  PlanDetailsCard: {
    borderColor: '#a4a7a9',
    PlanFeatures: {
      background: `linear-gradient(90deg, rgb(${primaryDarkerColor}) 0%, rgb(${primaryColor}) 100%)`,
      color: theme.palette.primary.contrastText,
      '.plan-features_header': {
        fontWeight: 500,
        fontSize: '24px',
      },
      '.feature-list li p': {
        fontWeight: 500,
        fontSize: '14px',
      },
    },
    ServiceCardBottomCss: {
      '.MuiAccordionSummary-content': {
        fontSize: '16px',
        fontWeight: '500',
        color: theme.palette.grey[800],
      },
      '.MuiAccordionSummary-expandIcon': {
        display: 'none',
      },
      '.select__button button': {
        backgroundColor: theme.palette.secondary.main,
        fontSize: '24px !important',
        fontWeight: '700 !important',
      },
      '.efs__button': {
        color: theme.palette.grey[800],
        fontSize: '16px',
        fontWeight: '500',
        svg: {
          display: 'none',
        },
      },
    },
  },
  Footer: {
    logoHeight: 48,
  },
});

/**
 * Theme Images
 */
theme.images = themeImages;

const themeColor = colorString.get.rgb(`rgb(${primaryColor})`);

const FirstThemeProvider = props => {
  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <link rel="shortcut icon" type="image/png" href={favicon} />
        <link rel="apple-touch-icon" href={favicon} />
        <meta name="theme-color" content={colorString.to.hex(themeColor)} />
        <link
          rel="stylesheet"
          type="text/css"
          href="https://cloud.typography.com/7037938/6897232/css/fonts.css"
        />
      </Helmet>
      {props.children}
    </ThemeProvider>
  );
};

export default FirstThemeProvider;
